import {
  Amount,
  BaseDocument,
  CommunicationsDocument,
  CommunicationsType,
  ContractDocument,
  ContractType,
  DispositionDocument,
  DocumentModuleOperation,
  ExpiredDocument,
  OfferingDocument,
  Practice,
  PracticeDocument,
  SignatureDocumentModuleType,
} from '../../model';
import {
  ContractMetadataBean,
  DispositionMetadataBean,
  DocumentBeanComplexMetadata,
  DocumentNestedGroup,
  InformationOfferMetadataBean,
} from '../generated/papyrus-metadata';
import { parseServerDateTime } from './commons-api-parsers';
import {
  getModuleOperation,
  parseBaseDocument,
  parseModuleInformation,
  parseModuleSignatory,
} from './common-document-parsers';
import {
  BundleState,
  BundleStateBundleStatusEnum,
} from '../generated/signroom-orchestrator';
import {
  AMLQuestionnaireCodes,
  AMLQuestionnaireTypes,
  InvestmentProposalCodes,
  InvestmentProposalTypes,
  MiFiQuestionnaireCodes,
  MiFiQuestionnaireTypes,
  RtoImiOrderCodes,
  RtoImiOrderTypes,
} from '../../model/signature-area/signature-document-type-lists';
import { archivePracticeType } from '../../utils';
import { compareAsc } from 'date-fns';

type DocumentItemParser<T extends BaseDocument> = (
  item: DocumentBeanComplexMetadata,
  details?: BundleState,
  documents?: DocumentBeanComplexMetadata[]
) => T;

// DOCUMENTS LIST

const getPracticeAmount = (
  operations: DocumentModuleOperation[]
): Amount | undefined => {
  const opsWithAmount = operations?.filter(
    (e) =>
      e?.import?.value !== null &&
      e?.import?.value !== undefined &&
      !isNaN(e?.import?.value)
  );

  if (!opsWithAmount || opsWithAmount.length === 0) {
    return undefined;
  }

  const currency =
    opsWithAmount.find((e) => e.import?.currency)?.import?.currency || '';
  const totalValue = opsWithAmount.reduce(
    (sum, op) => sum + op.import!.value,
    0
  );

  return {
    value: totalValue,
    currency: currency,
  };
};

const parseCode = (
  bundle: DocumentBeanComplexMetadata
): SignatureDocumentModuleType => {
  if (
    AMLQuestionnaireCodes.includes(
      bundle.documentType as AMLQuestionnaireTypes
    ) ||
    MiFiQuestionnaireCodes.includes(
      bundle.documentType as MiFiQuestionnaireTypes
    )
  ) {
    return 'QuestionnaireModule';
  }
  if (RtoImiOrderCodes.includes(bundle.documentType as RtoImiOrderTypes)) {
    return 'RtoImiModule';
  }
  if (
    InvestmentProposalCodes.includes(
      bundle.documentType as InvestmentProposalTypes
    )
  ) {
    return 'ServiceActivationModule';
  }

  return 'GenericModule';
};

export const parseContractDocumentItem: DocumentItemParser<ContractDocument> = (
  item
) => {
  const metadata = item.complexMetadata as ContractMetadataBean;

  return {
    ...parseBaseDocument<ContractType>(item),
    signDate: metadata.signDate ? new Date(metadata.signDate) : undefined,
  };
};

export const parseCommunicationDocumentItem: DocumentItemParser<
  CommunicationsDocument
> = (item) => {
  return {
    ...parseBaseDocument<CommunicationsType>(item),
    jurisdictionDate: item.accrualDate
      ? parseServerDateTime(item.accrualDate)
      : undefined,
  };
};

export const parseOfferingDocumentItem: DocumentItemParser<OfferingDocument> = (
  item
) => {
  const complexMetadata = item.complexMetadata as InformationOfferMetadataBean;
  return {
    ...parseBaseDocument(item),
    company: complexMetadata.company === 'ALLFUNDS' ? 'ALLFUNDSBANK' : 'Azimut',
    flagExternalLink: false,
    type: 'Letter',
  };
};

export const parseExpiredDocumentItem: DocumentItemParser<ExpiredDocument> = (
  item,
  details,
  documents
) => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;
  const moduleOperation = getModuleOperation(
    item,
    complexMetadata,
    documents || []
  );

  return {
    ...parseBaseDocument(item, details),
    code: parseCode(item),
    practiceNumber: complexMetadata.planId || '',
    signDate: complexMetadata.signDate
      ? new Date(complexMetadata.signDate)
      : undefined,
    uploadDate: item.creationDate
      ? parseServerDateTime(item.creationDate)
      : undefined,
    amount:
      item.documentType !== 'KIID'
        ? getPracticeAmount(moduleOperation)
        : undefined,
    moduleInformation: details
      ? parseModuleInformation(item, details, complexMetadata)
      : undefined,
    moduleOperation,
    moduleSignatory: details ? parseModuleSignatory(details) : undefined,
  } as ExpiredDocument;
};

export const parseDispositionDocumentItem: any = (
  item: DocumentBeanComplexMetadata,
  details: BundleState,
  documents: any
) => {
  const complexMetadata = item.complexMetadata as DispositionMetadataBean;
  const moduleOperation = getModuleOperation(
    item,
    complexMetadata,
    documents || []
  );
  return {
    ...parseBaseDocument(item, details),
    code: parseCode(item),
    practiceNumber: complexMetadata.planId || '',
    signDate: complexMetadata.signDate
      ? new Date(complexMetadata.signDate)
      : undefined,
    uploadDate: item.creationDate
      ? parseServerDateTime(item.creationDate)
      : undefined,
    amount:
      item.documentType !== 'KIID'
        ? getPracticeAmount(moduleOperation)
        : undefined,
    moduleInformation: details
      ? parseModuleInformation(item, details, complexMetadata)
      : undefined,
    moduleOperation,
    moduleSignatory: details ? parseModuleSignatory(details) : undefined,
  };
};

// BUNDLES

//prettier-ignore
export const parseDocumentBundle = <T extends PracticeDocument>(
  bundles: { documents: DocumentBeanComplexMetadata[]; details: BundleState }[],
  itemParser: DocumentItemParser<T>
): any[] => {

  return bundles.map((bundle) => {
    const { details, documents } = bundle;
    const modules = documents.map((item) => itemParser(item, details, documents));
    const firstItem = documents[0] as any;

    const complexMetadata =
      firstItem.complexMetadata as DispositionMetadataBean;

    /*const customers: RecipientState[] = details.recipients
      ? details.recipients
          .filter((item) => item.userType === 'C')
          .sort((a, b) => a.routingOrder - b.routingOrder)
      : [];*/

    return {
      practiceNumber: complexMetadata.planId,
      customerName: firstItem.ownerName,
      customerCode: firstItem.ownerCode,
      documents: modules,
      uploadDate: details.creationDate?new Date(details.creationDate):new Date()
    };
  });
};

export const parseDispositionBundles = (
  bundles: { documents: DocumentBeanComplexMetadata[]; details: BundleState }[]
): Practice<DispositionDocument>[] =>
  parseDocumentBundle(bundles, parseDispositionDocumentItem);

export const parseExpiredDocumentsBundles = (
  bundles: { documents: DocumentBeanComplexMetadata[]; details: BundleState }[]
): Practice<ExpiredDocument>[] =>
  parseDocumentBundle(bundles, parseExpiredDocumentItem);

export const filterDocumentsBundle = (
  bundle: {
    [key: string]: DocumentNestedGroup;
  },
  status: BundleStateBundleStatusEnum[]
): DocumentNestedGroup[] =>
  Object.entries(bundle)
    .filter(
      ([_key, value]: [string, any]) =>
        value[1]?.length > 0 && status.includes(value[1][0].status)
    )
    .map(([_key, value]) => value[1]);

export const filterArchivedDocuments = (
  documents: DocumentBeanComplexMetadata[],
  status: BundleStateBundleStatusEnum[]
): DocumentBeanComplexMetadata[] =>
  documents?.filter((doc) =>
    status.includes(doc.status as BundleStateBundleStatusEnum)
  );

export const parseArchivedBundle = (
  documents: DocumentBeanComplexMetadata[],
  parserDocument: any
) => {

  const firstItem = documents[0] as any;
  const modules = documents.map((item) => parserDocument(item, {}, documents));
  //const complexMetadata = firstItem.complexMetadata as DispositionMetadataBean;
  const uploadDate = documents
    .map((item) =>
      parseServerDateTime(item.creationDate ? item.creationDate : '')
    )
    .sort(compareAsc)[0];

  return {
    practiceNumber: archivePracticeType,
    customerName: firstItem.ownerName,
    customerCode: firstItem.ownerCode,
    documents: modules,
    uploadDate: uploadDate,
  };
};

export const parseExpiredDocumentArchived = (
  documents: DocumentBeanComplexMetadata[]
): Practice<ExpiredDocument> => {
  return parseArchivedBundle(documents, parseExpiredDocumentItem);
};

export const parseDispositionArchived = (
  documents: DocumentBeanComplexMetadata[]
) => {
  return parseArchivedBundle(documents, parseDispositionDocumentItem);
};
